import {
  QuestionnaireCategoryItemsItemOneOf as Question,
} from '@/api/generated-catch-backend/model';
import { useMemo } from 'react';

function QuestionProgressBar({
  currentQuestionOnlyIndex,
  totalQuestions,
}: {
  currentQuestionOnlyIndex: number;
  totalQuestions: number;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: totalQuestions }, (_, i) => (
        <div
          key={i}
          className="h-[3px] flex-1 rounded-full transition-colors duration-300"
          style={{
            backgroundColor: i <= currentQuestionOnlyIndex
              ? 'var(--color-catch-avocado-400)'
              : '#e5e7eb',
          }}
        />
      ))}
    </div>
  );
}

function CategoryProgressBar({
  totalCategories,
  currentCategoryIndex,
  intraCategoryProgress,
}: {
  totalCategories: number;
  currentCategoryIndex: number;
  intraCategoryProgress: number;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: totalCategories }, (_, i) => {
        const isPast = i < currentCategoryIndex;
        const isCurrent = i === currentCategoryIndex;
        const fillPercent = isPast ? 100 : isCurrent ? intraCategoryProgress : 0;

        return (
          <div
            key={i}
            className="relative h-[3px] flex-1 overflow-hidden rounded-full"
            style={{ backgroundColor: '#e5e7eb' }}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-300"
              style={{
                width: `${fillPercent}%`,
                backgroundColor: 'var(--color-catch-avocado-400)',
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

interface HeaderProps {
  categoryName: string;
  currentQuestionOnlyIndex?: number;
  questions?: Question[];
  isInterstitial?: boolean;
  showProgress?: boolean;
  totalCategories?: number;
  currentCategoryIndex?: number;
  currentQuestionIndex?: number;
  questionToCategory?: Map<number, number>;
}

export function Header({
  categoryName,
  currentQuestionOnlyIndex = 0,
  questions = [],
  isInterstitial = false,
  showProgress = true,
  totalCategories,
  currentCategoryIndex,
  currentQuestionIndex,
  questionToCategory,
}: HeaderProps) {
  const useSectionCounter = totalCategories != null && totalCategories > 1;

  // Memoize intra-category progress to avoid O(n) map iteration on every render
  const intraCategoryProgress = useMemo(() => {
    if (!useSectionCounter || !questionToCategory || currentQuestionIndex == null || currentCategoryIndex == null) {
      return 0;
    }
    let categoryQuestionCount = 0;
    let questionsBeforeCurrent = 0;
    for (const [qIdx, catIdx] of questionToCategory.entries()) {
      if (catIdx === currentCategoryIndex) {
        categoryQuestionCount++;
        if (qIdx < currentQuestionIndex) {
          questionsBeforeCurrent++;
        }
      }
    }
    if (categoryQuestionCount === 0) return 0;
    return Math.round(((questionsBeforeCurrent + 1) / categoryQuestionCount) * 100);
  }, [useSectionCounter, questionToCategory, currentQuestionIndex, currentCategoryIndex]);

  // Interstitial mode: hide header entirely (back button lives in StickyFooterBar)
  if (isInterstitial) {
    return null;
  }

  const counterText = useSectionCounter && currentCategoryIndex != null
    ? `${currentCategoryIndex + 1}/${totalCategories}`
    : `${currentQuestionOnlyIndex + 1}/${questions.length}`;

  return (
    <div className="flex w-full flex-col px-6 pt-safe-offset-4 pb-3">
      <div className="flex w-full items-center justify-between py-2">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-900">
          {categoryName}
        </span>
        <span className="text-xs font-medium tabular-nums text-gray-400">
          {counterText}
        </span>
      </div>
      {showProgress && (
        useSectionCounter && currentCategoryIndex != null ? (
          <CategoryProgressBar
            totalCategories={totalCategories}
            currentCategoryIndex={currentCategoryIndex}
            intraCategoryProgress={intraCategoryProgress}
          />
        ) : (
          <QuestionProgressBar
            currentQuestionOnlyIndex={currentQuestionOnlyIndex}
            totalQuestions={questions.length}
          />
        )
      )}
    </div>
  );
}
